import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'

import styled, { css } from 'styled-components'

import Pagination from 'components/Pagination'
import Table from 'components/Table'
import Tooltip from 'components/Tooltip'
import HelpVideoLink from 'components/HelpVideoLink'

import { color } from 'styles/colors'
import { PanelTitle, screenSize } from 'styles/shared'

import { NotificationRecipient } from 'types/NotificationRecipient'

type Props = {
  notificationRecipients: NotificationRecipient[]
}

const List = ({ notificationRecipients = [] }: Props) => {
  const [currentPage, setCurrentPage] = useState(1)

  const alertSummary = (recipient: NotificationRecipient) => {
    const alerts : string[] = []

    if (recipient.majorAlerts) { alerts.push('Major') }
    if (recipient.criticalAlerts) { alerts.push('Critical') }
    if (recipient.sdaAlerts) { alerts.push('SDA') }
    if (recipient.companyUpdateAlerts) { alerts.push('Company Update') }

    return alerts.length === 0 ? 'None' : alerts.join(', ')
  }

  const startIndex = (currentPage - 1) * 10
  const endIndex = startIndex + 10
  const pageResult = notificationRecipients.slice(startIndex, endIndex)

  return (
    <>
      <S.Header>
        <S.PanelHeader>
          <S.PanelTitle>
            {notificationRecipients.length + ' '}
            Notification Recipient
            {notificationRecipients.length > 1 || notificationRecipients.length === 0 ? 's' : ''}
          </S.PanelTitle>
          <S.HelpVideoLink videoKey="notifications" />
        </S.PanelHeader>

        <S.Pagination>
          {notificationRecipients.length > 10 && (
            <Pagination
              pageCount={Math.ceil(notificationRecipients.length / 10)}
              page={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </S.Pagination>
      </S.Header>

      <Table
        items={notificationRecipients}
        noDataMessage={{
          title: 'No Recipients',
          description: 'Click "Add Recipient" to add your first notification recipient.'
        }}
      >
        <S.TableHeader>
          <S.TableHeaderCell title="Name" />
          <S.TableHeaderCell title="Email" />
          <S.TableHeaderCell title="Selected Alerts" />
        </S.TableHeader>

        {pageResult.map(recipient => (
          <S.Row key={recipient.id}>
            <S.NameCell>
              <S.NameLink to={`/notifications/${recipient.id}/edit`}>{recipient.name}</S.NameLink>
            </S.NameCell>

            <S.EmailCell>
              <S.Email>
                {recipient.email}
              </S.Email>

              {recipient.isVerificationTokenExpired
                ? (
                  <Tooltip
                    title="The recipient was sent a verification link to verify their address but did not accept in 24 hours. Click &quot;resend verification&quot; to send the recipient a new verification email."
                  >
                    <S.ExpiredStatus>expired verification</S.ExpiredStatus>
                  </Tooltip>
                )
                : recipient.isVerificationPending &&
                <Tooltip
                  title="The recipient was sent a verification link to verify their address. In order to begin receiving emails from Diagnostics, the recipient must click Accept in the verification email."
                >
                  <S.PendingStatus>pending verification</S.PendingStatus>
                </Tooltip>
              }
            </S.EmailCell>
            <S.Cell>{alertSummary(recipient)}</S.Cell>
          </S.Row>
        ))}
      </Table>
    </>
  )
}

export default List

const cellStyle = css`
  @media ${screenSize.medium} {
    font-size: 12px;
  }
`

const S = {
  TableHeader: styled(Table.Header)`
    ${cellStyle}
  `,
  PanelTitle: styled(PanelTitle)`
    color: ${color('primaryText')};
    align-self: flex-end;

    @media ${screenSize.medium} {
      font-size: 16px;
      align-self: flex-start;
    }
  `,
  HelpVideoLink: styled(HelpVideoLink)`
    margin-top: -3px;

    @media ${screenSize.medium} {
      margin-top: 1px;
    }
  `,
  Row: styled(Table.Row)`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  `,
  NameCell: styled(Table.Cell)`
    ${cellStyle} 
    line-height: 25px;
    padding-left: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin: 15px 0;
  `,
  EmailCell: styled(Table.Cell)`
    flex-wrap: wrap;
    padding-left: 10px;
    align-items: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  Cell: styled(Table.Cell)`
   ${cellStyle} 
    flex-wrap: wrap;
    align-items: center;
    padding-left: 10px;
  `,
  ExpiredStatus: styled.i`
    ${cellStyle} 
    text-transform: uppercase;
    color: ${color('errorText')};
  `,
  PendingStatus: styled.i`
    ${cellStyle} 
    text-transform: uppercase;
    color: ${color('ancillaryText')};
  `,
  Header: styled.div`
    width: 100%;
    display: flex;
    padding: 10px;
    justify-content: space-between;

    @media ${screenSize.small} {
      justify-content: unset;
      flex-direction: column;
    }
  `,
  Email: styled.div`
    ${cellStyle} 
    margin-right: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  NameLink: styled(NavLink)`
    ${cellStyle} 
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    padding: 0 5px;
  `,
  Pagination: styled.div`
    ${cellStyle} 
    display: inline-block;
    white-space: nowrap;
  `,
  PanelHeader: styled.div`
    ${cellStyle} 
    display: flex;
    align-content: center;
    justify-content: space-between;
  `,
  TableHeaderCell: styled(Table.HeaderCell)`
    @media ${screenSize.medium} {
      padding-left: 10px;
    }
  `
}
